package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.screenshots.design.derive.resizable
import dev.lingvo.shared.screenshots.design.derive.DeriveDeviceStatus
import dev.lingvo.shared.screenshots.design.derive.DeriveDevicesRequest
import dev.lingvo.shared.screenshots.design.derive.MagicResize
import dev.lingvo.site.components.studio.preview.DesignLooksPort
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LModalSize
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.dom.Div

/** The dialog freezes its saved base so neither autosave nor an in-flight acceptance can replace newer edits. */
@Composable
internal fun DesignResize(
    data: DesignPageData, controller: DesignAutosaveController, device: DeviceClass, locale: String,
    looks: DesignLooksPort, runner: ScreenshotsActionRunner, writable: Boolean, onOpen: (Boolean) -> Unit, canOpen: Boolean = true,
) {
    var open by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }
    var failure by remember { mutableStateOf<String?>(null) }
    val document = controller.document ?: return
    val source = document.boards.firstOrNull { it.deviceClass == device.id } ?: return
    source.derivation?.let {
        LMuted(trf("screenshots.resize.derived", "Derived from {device}. Editing this device makes it an independent layout.",
            "device" to (DeviceClass.fromId(it.sourceDevice)?.displayName ?: it.sourceDevice)))
    }
    if (!open) failure?.let { LAlert(title = it) }
    LButton(t("app.projects.slug.screenshots.design_resize.derive_device_sizes", "Derive device sizes"), onClick = { open = true; busy = false; failure = null; onOpen(true) },
        enabled = writable && canOpen && looks.available && source.derivation == null && controller.state == DesignSaveState.CLEAN,
        variant = LButtonVariant.SECONDARY)
    if (!open) return
    DisposableEffect(Unit) { onDispose { onOpen(false) } }
    val original = remember { document }
    val base = remember { controller.version }
    LaunchedEffect(document, controller.version, writable) {
        if (document != original || controller.version != base || !writable) { open = false; onOpen(false) }
    }
    var selected by remember { mutableStateOf<Set<String>>(emptySet()) }
    val targets = remember(original, device) { DeviceClass.forPlatform(original.platform).filter { it != device && it.resizable } }
    val candidates = remember(original, device) { targets.associate { target ->
        target.id to MagicResize.devices(original, device.id, listOf(target.id))
    } }
    var checks by remember(original) { mutableStateOf<Map<String, ResizeFitCheck>>(emptyMap()) }
    var preview by remember { mutableStateOf(targets.first().id) }
    var previewLocale by remember { mutableStateOf(locale) }
    val available = targets.filter { candidates.getValue(it.id).devices.single().status != DeriveDeviceStatus.OVERRIDE &&
        candidates.getValue(it.id).devices.single().status != DeriveDeviceStatus.REFUSED }.map { it.id }.toSet()
    val accepted = available.filter { checks[it]?.accepted == true }.toSet()
    val batch = remember(original, selected) { if (selected.isEmpty()) null else MagicResize.devices(original, device.id, selected.toList()) }
    val selectionValid = selected.isNotEmpty() && selected.all { it in accepted } && batch?.devices?.none { it.status == DeriveDeviceStatus.REFUSED } == true
    fun close() { if (!busy) { open = false; onOpen(false) } }
    fun apply(devices: Set<String>) {
        if (busy || devices.isEmpty() || !accepted.containsAll(devices)) return
        val candidate = MagicResize.devices(original, device.id, devices.toList())
        if (candidate.devices.any { it.status == DeriveDeviceStatus.REFUSED }) {
            failure = tr("app.projects.slug.screenshots.design_resize.this_selection_exceeds_the_design_limits", "This selection exceeds the design limits. Select fewer devices.")
            return
        }
        runner.run(ScreenshotsPrivilege.DESIGN, { busy = it }, {
                deriveDesignDevices(data.project.slug, original.platform, DeriveDevicesRequest(base, device.id, devices.toList(), false))
            }, { error ->
                if (error is dev.lingvo.site.api.ApiException && error.code == "design_version_conflict") {
                    controller.reconcileConflict(error)
                    open = false; onOpen(false)
                }
                failure = if (error is dev.lingvo.site.api.ApiException && error.status == 409)
                    tr("app.projects.slug.screenshots.design_resize.the_design_changed_close_this_preview", "The design changed. Close this preview and reload before deriving devices.")
                else tr("app.projects.slug.screenshots.design_resize.the_devices_could_not_be_derived", "The devices could not be derived. Retry after checking your connection.")
            }, { response ->
                val saved = response.saved
                val refused = response.devices.filter { it.status == DeriveDeviceStatus.REFUSED }.map { it.device }
                val notice = refused.takeIf { it.isNotEmpty() }?.let {
                    trf("screenshots.resize.partial", "These devices were refused: {devices}. Review the remaining devices before retrying.",
                        "devices" to it.joinToString(", ") { id -> DeviceClass.fromId(id)?.displayName ?: id })
                }
                if (saved == null && response.devices.none { it.status == DeriveDeviceStatus.REFUSED }) {
                    open = false; onOpen(false)
                } else if (saved == null && notice != null) {
                    failure = notice
                    selected = selected - refused.toSet()
                } else if (saved == null || !controller.adoptRestored(saved, base, original)) {
                    failure = tr("app.projects.slug.screenshots.design_resize.reload_the_design_to_view_the", "Reload the design to view the accepted devices.")
                } else { failure = notice; open = false; onOpen(false) }
            })
    }
    LModal(true, t("app.projects.slug.screenshots.design_resize.preview_derived_devices", "Preview derived devices"), ::close, size = LModalSize.LG,
        modifier = Modifier.styleModifier { property("max-width", "min(72rem, calc(100vw - 24px))") }) {
        LMuted(t("app.projects.slug.screenshots.design_resize.review_each_device_before_accepting_existing", "Review each device before accepting. Existing device edits take precedence. Captures are needed for each target device."))
        failure?.let { LAlert(title = it) }
        candidates.forEach { (id, candidate) ->
            if (id in available) ResizeFit(original = candidate.document, data = data, device = id, looks = looks) { check ->
                checks = checks + (id to check)
            }
        }
        Div(attrs = { attr("role", "status"); attr("aria-live", "polite") }) {
            LMuted(trf("screenshots.resize.progress", "Checked {checked} of {total} devices.",
                "checked" to checks.size, "total" to available.size))
        }
        Column(Modifier.gap(Space.SM)) {
            targets.forEach { target ->
                val outcome = candidates.getValue(target.id).devices.single()
                val status = when {
                    outcome.status == DeriveDeviceStatus.OVERRIDE -> tr("app.projects.slug.screenshots.design_resize.existing_device_override_retained", "Existing device override retained")
                    outcome.status == DeriveDeviceStatus.REFUSED -> tr("app.projects.slug.screenshots.design_resize.cannot_derive_within_the_design_limits", "Cannot derive within the design limits")
                    checks[target.id] == null -> tr("app.projects.slug.screenshots.design_resize.checking_all_text_and_languages", "Checking all text and languages…")
                    checks[target.id]?.accepted != true -> checks.getValue(target.id).message
                    else -> tr("app.projects.slug.screenshots.design_resize.text_fits_in_every_language", "Text fits in every language")
                }
                LCheckbox(target.id in selected, { checked -> selected = if (checked) selected + target.id else selected - target.id },
                    label = "${target.displayName} — $status", modifier = Modifier.styleModifier { property("min-height", "40px") },
                    enabled = !busy && target.id in accepted)
            }
        }
        LSelect(preview, targets.map { LSelectOption(it.id, it.displayName) }, { preview = it },
            label = t("app.projects.slug.screenshots.design_resize.preview_device", "Preview device"))
        LSelect(previewLocale, (listOf(data.project.sourceLocale) + data.project.locales).distinct().map {
            LSelectOption(it, it)
        }, { previewLocale = it }, label = t("app.projects.slug.screenshots.design_resize.preview_language", "Preview language"))
        Row(Modifier.gap(Space.MD).styleModifier { property("flex-wrap", "wrap"); property("min-width", "0") }) {
            Column(Modifier.styleModifier { property("flex", "1 1 240px"); property("min-width", "0") }) {
                LMuted(t("app.projects.slug.screenshots.design_resize.before_source_design", "Before: source design"))
                ShotPreviewGrid(data, original, device, previewLocale, looks, onFits = {}, allLocales = false, firstOnly = true, priority = true, links = false)
            }
            Column(Modifier.styleModifier { property("flex", "1 1 240px"); property("min-width", "0") }) {
                LMuted(t("app.projects.slug.screenshots.design_resize.after_target_device", "After: target device"))
                val candidate = candidates.getValue(preview)
                if (candidate.devices.single().status != DeriveDeviceStatus.REFUSED) ShotPreviewGrid(data, candidate.document,
                    requireNotNull(DeviceClass.fromId(preview)), previewLocale, looks, onFits = {}, allLocales = false, firstOnly = true, priority = true, links = false)
            }
        }
        Row(Modifier.gap(Space.SM).styleModifier { property("flex-wrap", "wrap") }) {
            LButton(t("app.projects.slug.screenshots.design_resize.accept_selected", "Accept selected"), onClick = { apply(selected) }, enabled = !busy && selectionValid)
            LButton(t("app.projects.slug.screenshots.design_resize.accept_all_fitting_devices", "Accept all fitting devices"), onClick = { apply(accepted) },
                enabled = !busy && available.isNotEmpty() && checks.keys.containsAll(available) && accepted.isNotEmpty(), variant = LButtonVariant.SECONDARY)
            LButton(t("app.projects.slug.screenshots.design_resize.close", "Close"), onClick = ::close, enabled = !busy, variant = LButtonVariant.GHOST)
        }
        if (batch?.devices?.any { it.status == DeriveDeviceStatus.REFUSED } == true) LAlert(title = t("app.projects.slug.screenshots.design_resize.this_selection_exceeds_the_design_limits", "This selection exceeds the design limits. Select fewer devices."))
    }
}
